import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../../api/client';
import {
  updateStudioVisibility,
  type StudioDetail,
  type StudioVisibilityAction,
} from '../../api/partnerStudios';
import { useToast } from '../../components/ui/ToastProvider';
import { usePartnerContext } from './PartnerLayout';

const STUDIO_STATUS_LABELS: Record<string, string> = {
  DRAFT: '작성 중',
  PENDING_ACTIVATION: '승인 대기',
  ACTIVE: '노출 중',
  HIDDEN: '숨김',
  SUSPENDED: '정지',
};

function studioBadgeClass(status: string) {
  if (status === 'ACTIVE') return 'partner-badge partner-badge--green';
  if (status === 'PENDING_ACTIVATION') return 'partner-badge partner-badge--yellow';
  if (status === 'SUSPENDED') return 'partner-badge partner-badge--red';
  return 'partner-badge';
}

export function PartnerStudiosPage() {
  const { studios, studiosLoading, studiosError, reloadStudios } = usePartnerContext();
  const { showToast } = useToast();
  const [actingId, setActingId] = useState<string | null>(null);

  const changeVisibility = async (studio: StudioDetail, action: StudioVisibilityAction) => {
    if (actingId) return;
    setActingId(studio.studioId);
    try {
      await updateStudioVisibility(studio.studioId, action);
      showToast(action === 'HIDE' ? '스튜디오를 숨겼습니다.' : '활성화를 요청했습니다.');
      await reloadStudios();
    } catch (error) {
      showToast(
        error instanceof ApiError && error.message ? error.message : '처리에 실패했습니다.',
      );
    } finally {
      setActingId(null);
    }
  };

  return (
    <>
      <h1 className="partner-title">스튜디오 관리</h1>
      <p className="partner-subtitle">스튜디오 정보를 관리하고 룸을 등록하세요.</p>

      <div className="partner-toolbar">
        <div className="partner-toolbar__spacer" />
        <Link className="partner-btn" to="/partner/studios/new">
          + 스튜디오 등록
        </Link>
      </div>

      {studiosError ? <div className="partner-error">{studiosError}</div> : null}

      {studiosLoading ? (
        <p className="partner-empty">불러오는 중…</p>
      ) : studios.length === 0 ? (
        <div className="partner-card">
          <p className="partner-empty">
            아직 등록된 스튜디오가 없습니다. 첫 스튜디오를 등록해보세요.
          </p>
        </div>
      ) : (
        studios.map((studio) => {
          const acting = actingId === studio.studioId;
          return (
            <div className="partner-card" key={studio.studioId}>
              <h2 className="partner-card__title">
                {studio.name}
                <span className={studioBadgeClass(String(studio.status))}>
                  {STUDIO_STATUS_LABELS[String(studio.status)] ?? studio.status}
                </span>
              </h2>
              <dl className="partner-detail-grid" style={{ marginBottom: 16 }}>
                <div>
                  <dt>주소</dt>
                  <dd>{studio.address?.roadAddress ?? '—'}</dd>
                </div>
                <div>
                  <dt>연락처</dt>
                  <dd>{studio.contactPhone ?? '—'}</dd>
                </div>
                <div>
                  <dt>슬러그</dt>
                  <dd>{studio.slug ?? '—'}</dd>
                </div>
                <div>
                  <dt>갤러리</dt>
                  <dd>{studio.gallery.length}장</dd>
                </div>
              </dl>
              <div className="partner-form__actions" style={{ justifyContent: 'flex-start' }}>
                <Link
                  className="partner-btn partner-btn--ghost partner-btn--sm"
                  to={`/partner/studios/${studio.studioId}`}
                >
                  정보 수정
                </Link>
                <Link
                  className="partner-btn partner-btn--ghost partner-btn--sm"
                  to={`/partner/studios/${studio.studioId}/rooms`}
                >
                  룸 관리
                </Link>
                {String(studio.status) === 'ACTIVE' ? (
                  <button
                    className="partner-btn partner-btn--danger partner-btn--sm"
                    disabled={acting}
                    onClick={() => changeVisibility(studio, 'HIDE')}
                    type="button"
                  >
                    숨기기
                  </button>
                ) : (
                  <button
                    className="partner-btn partner-btn--sm"
                    disabled={acting}
                    onClick={() => changeVisibility(studio, 'REQUEST_ACTIVATION')}
                    type="button"
                  >
                    활성화 요청
                  </button>
                )}
              </div>
            </div>
          );
        })
      )}
    </>
  );
}
